/**
 * QuizSubmissionsV2 - Quiz Activity Submissions with modern design
 * Features:
 * - Summary statistics (Total Students, Total Submissions)
 * - Search bar with debounce
 * - View dropdown for column toggling (Roll No, TA)
 * - Actions: Force Run, Download Marks, Download All Files
 * - Table with sorting and pagination
 * - View Submission, Download Files, Delete Submission actions per row
 */
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Dropdown, Spinner, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import displayGlobalMessage, { toastConfig } from 'Utils/DisplayGlobalMessage';
import { toast } from 'react-toastify';
import log from 'loglevel';
import { loadCoursesListRequest } from 'Screens_old/PublicCourses/Common/Actions/Actions';
import { loadCoursesListRequest as instructor_loadCoursesListRequest } from 'Store/MyDashboard/Instructor/Actions';

// Icons
import { FiUsers, FiSearch, FiChevronDown, FiDownload, FiEye, FiTrash2, FiChevronUp, FiChevronsUp } from 'react-icons/fi';
import { BsClipboardCheck } from 'react-icons/bs';
import { VscRunAll } from 'react-icons/vsc';
import { SlRefresh } from 'react-icons/sl';

import styles from './QuizSubmissionsV2.module.css';

const DEFAULT_PAGE_SIZE = 15;

// Stat Card component
const StatCard = memo(({ label, value, icon: Icon, colorClass }) => (
  <div className={styles.statCard}>
    <div className={styles.statCardContent}>
      <span className={styles.statCardLabel}>{label}</span>
      <span className={`${styles.statCardValue} ${styles[colorClass]}`}>{value}</span>
    </div>
    <div className={`${styles.statCardIcon} ${styles[colorClass + 'Bg']}`}>
      <Icon size={18} />
    </div>
  </div>
));

const QuizSubmissionsV2 = () => {
  const { courseId, labId, activityID } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const navEl = document.querySelector('.sticky-top');
    const navH = navEl ? navEl.getBoundingClientRect().height : 60;
    document.documentElement.style.setProperty('--bt-navbar-h', `${navH}px`);
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Data state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsSummary, setSubmissionsSummary] = useState({});
  const [labTitle, setLabTitle] = useState('');
  const [activityTitle, setActivityTitle] = useState('');
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lazyLoading, setLazyLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [forceRunLoading, setForceRunLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const tableWrapperRef = useRef(null);

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [sortField, setSortField] = useState('email');
  const [sortOrder, setSortOrder] = useState('');

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    rollNo: false,
    ta: false,
  });

  // Dropdown states
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  // Course name
  const [courseName, setCourseName] = useState('');
  const instructorCourses = useSelector((state) => state.instructorCoursesMap);
  const courses = useSelector((state) => state.publicCoursesMap);

  // Load course lists
  useEffect(() => {
    dispatch(loadCoursesListRequest());
    dispatch(instructor_loadCoursesListRequest());
  }, [dispatch]);

  // Fetch submissions summary
  const fetchSubmissionsSummary = useCallback(async (showMessage = false) => {
    setIsSummaryLoading(true);
    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/summary/`;
      const response = await httpService.get(url);
      setSubmissionsSummary(response.data);
      if (showMessage) {
        displayGlobalMessage('Refreshed', 'success');
      }
    } catch (error) {
      log.error('Error fetching summary:', error);
      if (showMessage) {
        displayGlobalMessage('Failed to refresh summary!', 'error');
      }
    } finally {
      setIsSummaryLoading(false);
    }
  }, [activityID]);

  // Fetch submissions data
  const fetchSubmissions = useCallback(async () => {
    const isFirstPage = currentPage === 1;
    if (isFirstPage) {
      setLazyLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    try {
      let url = `${constants.base_url}/otherlabs/activity/${activityID}/other_activity_submissions/?page=${currentPage}&page_size=${pageSize}`;

      if (debouncedSearchQuery) {
        url += `&search_query=${debouncedSearchQuery}`;
      }
      if (sortField) {
        url += `&sort=${sortOrder}${sortField}`;
      }

      const response = await httpService.get(url);
      const data = response.data;

      const newResults = data.data || [];
      const count = data.total_count || 0;

      if (isFirstPage) {
        setSubmissions(newResults);
      } else {
        setSubmissions((prev) => [...prev, ...newResults]);
      }
      setHasMore(currentPage * pageSize < count);

      // Fetch quiz data if not already loaded
      if (!quizData) {
        const quizUrl = `${constants.base_url}/lab/activity/${activityID}?type=other`;
        const quizResponse = await httpService.get(quizUrl);
        setQuizData(quizResponse.data);
        setLabTitle(quizResponse.data?.other_activity?.lab_data?.title || '');
        setActivityTitle(quizResponse.data?.other_activity?.name || '');
      }
    } catch (error) {
      log.error('Error fetching submissions:', error);
      displayGlobalMessage('Error fetching submissions', 'error');
    } finally {
      setLazyLoading(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      setLoading(false);
    }
  }, [activityID, currentPage, pageSize, debouncedSearchQuery, sortField, sortOrder, quizData]);

  // Initial load
  useEffect(() => {
    fetchSubmissionsSummary();
  }, [fetchSubmissionsSummary]);

  // Reset to page 1 when search/sort changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setSubmissions([]);
  }, [debouncedSearchQuery, sortField, sortOrder]);

  useEffect(() => {
    fetchSubmissions();
  }, [currentPage, debouncedSearchQuery, sortField, sortOrder]);

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || isLoadingMoreRef.current || !hasMore) return;
    if (wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 100) {
      isLoadingMoreRef.current = true;
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore]);

  // Set course name from redux store
  useEffect(() => {
    let tempCourse;
    if (courses?.length > 0) {
      tempCourse = courses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
    if (!tempCourse && instructorCourses?.length > 0) {
      tempCourse = instructorCourses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
  }, [courses, instructorCourses, courseId]);

  // Force Run submissions
  const handleForceRun = async () => {
    let toastId = '';
    try {
      setForceRunLoading(true);
      toastId = toast.loading(
        "This process may take some time. Please avoid reloading this page.",
        toastConfig
      );
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/force_run_submissions/`;
      await httpService.post(url);
      toast.update(toastId, {
        render: 'Force run operation successful.',
        type: 'success',
        ...toastConfig,
        isLoading: false,
      });
      fetchSubmissions();
      fetchSubmissionsSummary();
    } catch (error) {
      log.error(error);
      toast.update(toastId, {
        render: 'Error occurred',
        type: 'error',
        ...toastConfig,
        isLoading: false,
      });
    } finally {
      setForceRunLoading(false);
    }
  };

  // Download marks
  const handleDownloadMarks = async () => {
    // Check if there are any submissions
    if (!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) {
      displayGlobalMessage('No submissions to download', 'error');
      return;
    }

    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/export_question_wise_marks/`;
      const response = await httpService.get(url, { responseType: 'blob' });

      const filename = `activity_${activityID}_question_wise_marks.csv`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      displayGlobalMessage('Download successful', 'success');
    } catch (error) {
      log.error('Error downloading marks:', error);
      displayGlobalMessage('Failed to download marks', 'error');
    }
  };

  // Download all files
  const handleDownloadAllFiles = async () => {
    // Check if there are any submissions
    if (!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) {
      displayGlobalMessage('No submissions to download', 'error');
      return;
    }

    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/other_submission_download_lab_files_all_students/`;
      const response = await httpService.get(url, { responseType: 'blob' });

      const filename = `${activityID}_lab_files_submitted.tar.gz`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      displayGlobalMessage('Download successful', 'success');
    } catch (error) {
      log.error('Error downloading files:', error);
      displayGlobalMessage('Failed to download files', 'error');
    }
  };

  // Download files for single student
  const handleDownloadFiles = async (rowData) => {
    try {
      const url = `${constants.base_url}/otherlabs/activity/${rowData.lab_id}/other_submission_download_lab_files/?stud_id=${encodeURIComponent(rowData.student_id)}&username=${encodeURIComponent(rowData.first_name)}`;
      const response = await httpService.get(url, { responseType: 'blob' });

      let filename = `${rowData.student_id}_lab_${rowData.lab_id}_files.tar.gz`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      log.error('Error downloading file:', error);
      displayGlobalMessage('Failed to download file', 'error');
    }
  };

  // View submission
  const handleViewSubmission = (rowData) => {
    const studentSubmissionUrl = `${location.pathname}/${rowData.email}`;
    window.open(studentSubmissionUrl, '_blank');
  };

  // Delete submission
  const handleDeleteSubmission = async (rowData) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this submission?');
    if (!confirmDelete) return;

    try {
      const url = `${constants.base_url}/otherlabs/activity/${activityID}/student_submission/?student_id=${rowData.email}`;
      const response = await httpService.delete(url);
      if (response.data?.msg) {
        displayGlobalMessage(response.data.msg, 'success');
      }
      fetchSubmissions();
      fetchSubmissionsSummary();
    } catch (error) {
      log.error(error);
      displayGlobalMessage(error?.response?.data?.msg || 'Something went wrong', 'error');
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      if (sortOrder === '') {
        setSortOrder('-');
      } else {
        setSortOrder('');
        setSortField('email');
      }
    } else {
      setSortField(field);
      setSortOrder('');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <FiChevronsUp size={14} className={styles.sortIconDefault} />;
    return sortOrder === '-' ? <FiChevronDown size={14} /> : <FiChevronUp size={14} />;
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Select/Deselect all columns
  const toggleAllColumns = () => {
    const allSelected = Object.values(visibleColumns).every((v) => v);
    const newValue = !allSelected;
    setVisibleColumns({
      rollNo: newValue,
      ta: newValue,
    });
  };

  // Check if all columns are selected
  const allColumnsSelected = Object.values(visibleColumns).every((v) => v);

  // Memoized columns config
  const columnConfig = useMemo(() => [
    { key: 'rollNo', label: 'Roll No' },
    { key: 'ta', label: 'TA' },
  ], []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}` }}>
          {courseName || 'Course'}
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}/` }}>
          {labTitle || 'Lab'}
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labId}/other/${activityID}/view` }}>
          {activityTitle || 'Activity'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Submissions</Breadcrumb.Item>
      </Breadcrumb>

      {/* Statistics Row */}
      <div className={styles.statsRow}>
        <StatCard
          label="TOTAL STUDENTS"
          value={submissionsSummary.total_students || 0}
          icon={FiUsers}
          colorClass="blue"
        />
        <StatCard
          label="TOTAL SUBMISSIONS"
          value={submissionsSummary.total_submissions || 0}
          icon={BsClipboardCheck}
          colorClass="green"
        />
        <OverlayTrigger placement="top" overlay={<Tooltip id="refresh-tooltip">Refresh</Tooltip>}>
          <button
            className={styles.refreshBtn}
            onClick={() => fetchSubmissionsSummary(true)}
            disabled={isSummaryLoading}
          >
            <SlRefresh className={isSummaryLoading ? styles.spin : ''} size={16} />
          </button>
        </OverlayTrigger>
      </div>

      {/* Table Container */}
      <div className={styles.tableContainer}>
        {/* Search and Actions Bar */}
        <div className={styles.searchActionsBar}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              disabled={lazyLoading}
            />
          </div>

          <div className={styles.actionButtons}>

            {/* View Dropdown */}
            <Dropdown
              show={showViewDropdown}
              onToggle={(isOpen) => setShowViewDropdown(isOpen)}
            >
              <Dropdown.Toggle as="button" className={styles.actionBtn}>
                View
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.viewDropdownMenu}>
                <div className={styles.dropdownHeader}>TOGGLE COLUMNS</div>
                <Dropdown.Item
                  as="div"
                  className={styles.columnToggleItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={allColumnsSelected}
                      onChange={toggleAllColumns}
                    />
                    <span>Select All</span>
                  </label>
                </Dropdown.Item>
                <Dropdown.Divider />
                {columnConfig.map((col) => (
                  <Dropdown.Item
                    key={col.key}
                    as="div"
                    className={styles.columnToggleItem}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Actions Dropdown */}
            <Dropdown
              show={showActionsDropdown}
              onToggle={(isOpen) => setShowActionsDropdown(isOpen)}
            >
              <Dropdown.Toggle as="button" className={styles.actionBtn}>
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className={styles.actionsDropdownMenu}>
                <Dropdown.Item
                  onClick={handleDownloadMarks}
                  disabled={!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0}
                >
                  <FiDownload className={styles.dropdownIcon} />
                  Download Marks
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={handleDownloadAllFiles}
                  disabled={!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0}
                >
                  <FiDownload className={styles.dropdownIcon} />
                  Download All Files
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableWrapper} ref={tableWrapperRef} onScroll={handleScroll}>
          {lazyLoading && (
            <div className={styles.tableLoadingOverlay}>
              <Spinner size="sm" animation="border" />
              Loading...
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.snoCol}>S.NO.</th>
                <th className={`${styles.emailCol} ${styles.sortable}`} onClick={() => handleSort('email')}>
                  EMAIL {getSortIcon('email')}
                </th>
                <th className={`${styles.nameCol} ${styles.sortable}`} onClick={() => handleSort('first_name')}>
                  NAME {getSortIcon('first_name')}
                </th>
                {visibleColumns.rollNo && <th className={`${styles.rollCol} ${styles.sortable}`} onClick={() => handleSort('roll_no')}>ROLL NO {getSortIcon('roll_no')}</th>}
                {visibleColumns.ta && <th className={`${styles.taCol} ${styles.sortable}`} onClick={() => handleSort('TA')}>TA {getSortIcon('TA')}</th>}
                <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('obtained_manual_marks')}>
                  MARKS {getSortIcon('obtained_manual_marks')}
                </th>
                <th className={`${styles.marksCol} ${styles.sortable}`} onClick={() => handleSort('total_marks')}>
                  TOTAL MARKS {getSortIcon('total_marks')}
                </th>
                <th className={`${styles.statusCol} ${styles.sortable}`} onClick={() => handleSort('submission_status')}>SUBMISSION STATUS {getSortIcon('submission_status')}</th>
                <th className={styles.actionCol}>DOWNLOAD FILES</th>
                <th className={styles.actionCol}>VIEW SUBMISSION</th>
                <th className={styles.deleteCol}>DELETE SUBMISSION</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 ? (
                <tr>
                  <td colSpan={11} className={styles.emptyState}>
                    No available options
                  </td>
                </tr>
              ) : (
                submissions.map((submission, index) => (
                  <tr key={submission.email || index} className={styles.tableRow}>
                    <td className={styles.snoCell}>
                      {index + 1}
                    </td>
                    <td className={styles.emailCell}>{submission.email}</td>
                    <td className={styles.nameCell}>{submission.first_name}</td>
                    {visibleColumns.rollNo && (
                      <td className={styles.rollCell}>{submission.roll_no || '-'}</td>
                    )}
                    {visibleColumns.ta && (
                      <td className={styles.taCell}>{submission.TA || '-'}</td>
                    )}
                    <td className={styles.marksCell}>{submission.obtained_manual_marks ?? '-'}</td>
                    <td className={styles.marksCell}>{submission.total_marks ?? '-'}</td>
                    <td className={styles.statusCell}>
                      <span className={`${styles.statusBadge} ${
                        submission.submission_status === 'Submitted' ? styles.statusSuccess :
                        submission.submission_status === 'Not Uploaded' ? styles.statusDefault :
                        styles.statusWarning
                      }`}>
                        {submission.submission_status || '-'}
                      </span>
                    </td>
                    <td className={styles.actionCell}>
                      <button
                        className={styles.tableActionBtn}
                        onClick={() => {
                          if (submission.submission_status === "No Responses") {
                            displayGlobalMessage('This student has not submitted any responses', 'warning');
                            return;
                          }
                          handleDownloadFiles(submission);
                        }}
                      >
                        Download Files
                      </button>
                    </td>
                    <td className={styles.actionCell}>
                      <button
                        className={styles.tableActionBtn}
                        onClick={() => handleViewSubmission(submission)}
                      >
                        View Submission
                      </button>
                    </td>
                    <td className={styles.deleteCell}>
                      <button
                        className={styles.deleteBtn}
                        onClick={() => handleDeleteSubmission(submission)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {isLoadingMore && (
            <div className={styles.loadingMoreRow}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizSubmissionsV2;
